import { useState, useRef } from 'react';
import { logger } from '../utils/logger';
import { Upload, FileText, X, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import * as pdfjs from 'pdfjs-dist';
import { API_BASE_URL } from '../config/environment.js';
import { authFetch } from '../utils/authFetch';
import { enhanceTripWithImmediateFlightStatus } from '../services/immediateFlightStatusService.js';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

// Helper: generate a unique ID with a given prefix
const generateId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper: generate a PDF-prefixed confirmation code
const pdfConfirmation = () => 'PDF-' + Date.now();

// Helper function to parse flight date and time
const parseFlightDateTime = (dateStr, timeStr) => {
  try {
    // Parse the date (format: "2024-11-18")
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Parse the time (format: "12:26 PM" or "10:36 AM")
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!timeMatch) return new Date().getTime();
    
    let [, hours, minutes, period] = timeMatch;
    hours = parseInt(hours);
    minutes = parseInt(minutes);
    
    // Convert to 24-hour format
    if (period.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }
    
    // Create date object
    const date = new Date(year, month - 1, day, hours, minutes);
    return date.getTime();
  } catch (error) {
    console.error('Error parsing flight date/time:', error);
    return new Date().getTime();
  }
};

// Helper function to parse hotel dates without timezone issues
const parseHotelDate = (dateStr) => {
  try {
    if (!dateStr) return new Date().getTime();
    
    // If it's already a timestamp, return it
    if (typeof dateStr === 'number') return dateStr;
    
    // Handle various date formats that might come from PDF parsing
    let date;
    
    // Try parsing as ISO date string (YYYY-MM-DD)
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day, 12, 0, 0); // Use noon to avoid timezone issues
    }
    // Try parsing as MM/DD/YYYY or MM-DD-YYYY
    else if (dateStr.match(/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/)) {
      const parts = dateStr.split(/[/-]/);
      const month = parseInt(parts[0]) - 1;
      const day = parseInt(parts[1]);
      const year = parseInt(parts[2]);
      date = new Date(year, month, day, 12, 0, 0); // Use noon to avoid timezone issues
    }
    // Try parsing as a regular date string but force local timezone
    else {
      date = new Date(dateStr + 'T12:00:00'); // Add noon time to avoid timezone shift
    }
    
    return date.getTime();
  } catch (error) {
    console.error('Error parsing hotel date:', error, 'Input:', dateStr);
    return new Date().getTime();
  }
};

// ── PDF-parsed data mappers ───────────────────────────────────────────────────

// Helper: return the first truthy value, or a fallback
const fallback = (value, def) => value || def;

const mapParsedFlight = (flight) => {
  // If arrivalDate is missing, default to departureDate (same-day flight)
  const effectiveArrivalDate = flight.arrivalDate || flight.departureDate;
  
  const depTime = flight.departureDate && flight.departureTime
    ? parseFlightDateTime(flight.departureDate, flight.departureTime)
    : new Date().getTime();
  const arrTime = effectiveArrivalDate && flight.arrivalTime
    ? parseFlightDateTime(effectiveArrivalDate, flight.arrivalTime)
    : depTime + 2 * 60 * 60 * 1000; // Default: 2 hours after departure

  return {
    flightId: generateId('flight'),
    airline: fallback(flight.airline, 'Unknown'),
    flightNumber: fallback(flight.flightNumber, 'TBD'),
    departureAirport: fallback(flight.departureAirport, 'TBD'),
    departureTerminal: `${fallback(flight.departureAirport, 'TBD')} Airport`,
    departureGate: fallback(flight.departureGate, 'TBD'),
    departureTime: depTime,
    arrivalAirport: fallback(flight.arrivalAirport, 'TBD'),
    arrivalTerminal: `${fallback(flight.arrivalAirport, 'TBD')} Airport`,
    arrivalGate: fallback(flight.arrivalGate, 'TBD'),
    arrivalTime: arrTime,
    seatAssignment: fallback(flight.seatAssignment, 'TBD'),
    status: 'Confirmed',
    confirmationCode: fallback(flight.confirmationCode, pdfConfirmation()),
    frequentFlyerNumber: fallback(flight.frequentFlyerNumber, 'TBD'),
    aircraft: 'Unknown',
    distance: 'Unknown',
    duration: fallback(flight.duration, 'TBD'),
    cabin: 'Unknown'
  };
};

const mapParsedHotel = (hotel) => ({
  hotelId: generateId('hotel'),
  name: hotel.name || 'Hotel from PDF',
  address: hotel.address || 'Address from PDF',
  phoneNumber: '',
  checkInDate: hotel.checkIn ? parseHotelDate(hotel.checkIn) : new Date().getTime(),
  checkOutDate: hotel.checkOut ? parseHotelDate(hotel.checkOut) : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).getTime(),
  confirmationCode: hotel.confirmationNumber || pdfConfirmation(),
  roomType: 'Standard',
  rate: 0,
  totalRate: 0,
  currency: 'USD',
  frequentGuestNumber: '',
  specialInstructions: '',
  cancellationPolicy: ''
});

const mapParsedCarRental = (rental) => ({
  rentalId: generateId('rental'),
  company: rental.company || 'Car Rental from PDF',
  pickupLocation: rental.pickupLocation || 'Pickup from PDF',
  returnLocation: rental.returnLocation || 'Return from PDF',
  pickupDate: rental.pickupDate ? parseHotelDate(rental.pickupDate) : new Date().getTime(),
  returnDate: rental.returnDate ? parseHotelDate(rental.returnDate) : new Date().getTime() + 24 * 60 * 60 * 1000,
  pickupTime: rental.pickupTime || '',
  returnTime: rental.returnTime || '',
  confirmationCode: rental.confirmationNumber || pdfConfirmation(),
  status: 'Confirmed',
  frequentGuestNumber: rental.frequentGuestNumber || '',
  rateCode: '',
  dailyRate: 0,
  totalRate: 0,
  currency: 'USD',
  corporateDiscount: '',
  carType: rental.carType || 'Standard'
});

function computeTripDates(data) {
  const flightTimes = data.flights.map(flight => {
    // If arrivalDate is missing, default to departureDate (same-day flight)
    const effectiveArrivalDate = flight.arrivalDate || flight.departureDate;
    
    return {
      departure: flight.departureDate && flight.departureTime
        ? parseFlightDateTime(flight.departureDate, flight.departureTime)
        : new Date().getTime(),
      arrival: effectiveArrivalDate && flight.arrivalTime
        ? parseFlightDateTime(effectiveArrivalDate, flight.arrivalTime)
        : (flight.departureDate && flight.departureTime
          ? parseFlightDateTime(flight.departureDate, flight.departureTime) + 2 * 60 * 60 * 1000
          : new Date().getTime())
    };
  });

  if (flightTimes.length > 0) {
    const allTimes = flightTimes.flatMap(f => [f.departure, f.arrival]);
    return { start: Math.min(...allTimes), end: Math.max(...allTimes) };
  }

  return {
    start: parseHotelDate(data.dates?.startDate) || new Date().getTime(),
    end: parseHotelDate(data.dates?.endDate) || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).getTime()
  };
}

const PDFUploader = ({ onTripCreated, authToken, user }) => {
  // const [isUploading, setIsUploading] = useState(false); // Currently unused
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setParsedData(null);
      // Automatically parse the PDF
      await parseAndCreateTrip(file);
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setParsedData(null);
      // Automatically parse the PDF
      await parseAndCreateTrip(file);
    } else {
      toast.error('Please drop a valid PDF file');
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const parseAndCreateTrip = async (file) => {
    setIsParsing(true);
    try {
      // Parse the PDF
      const data = await parsePDF(file);
      setParsedData(data);
      toast.success('PDF parsed successfully!');
      
      // Automatically create the trip
      await createTripFromData(data, file.name);
    } catch (error) {
      console.error('PDF parsing or trip creation error:', error);
      toast.error('Failed to process PDF: ' + error.message);
    } finally {
      setIsParsing(false);
    }
  };

  const createTripFromData = async (data, fileName) => {
    try {
      const { start: tripStartDate, end: tripEndDate } = computeTripDates(data);

      // Debug: log parsed dates to help diagnose "trip complete" issue
      logger.debug('[PDFUploader] Parsed data from Bedrock:', JSON.stringify(data.flights?.map(f => ({
        departureDate: f.departureDate,
        departureTime: f.departureTime,
        arrivalDate: f.arrivalDate,
        arrivalTime: f.arrivalTime,
      })), null, 2));
      logger.debug('[PDFUploader] Computed trip dates:', {
        start: new Date(tripStartDate).toISOString(),
        end: new Date(tripEndDate).toISOString(),
        startMs: tripStartDate,
        endMs: tripEndDate,
      });

      // Get user ID from the user object
      const userId = user?.sub || user?.userId || user?.username;
      if (!userId) {
        throw new Error('User ID is required to create a trip');
      }

      const tripData = {
        userId: userId,
        tripName: data.tripName,
        company: 'PDF Import',
        travelerName: 'PDF Import',
        status: 'upcoming',
        startDate: tripStartDate,
        endDate: tripEndDate,
        purpose: `Trip from ${fileName}`,
        agencyRecordLocator: pdfConfirmation(),
        totalEstimatedCost: 0,
        currency: 'USD',
        flights: data.flights.map(mapParsedFlight),
        hotels: data.hotels.map(mapParsedHotel),
        carRentals: data.carRentals.map(mapParsedCarRental),
        expenses: {
          total: 0,
          currency: 'USD',
          reportStatus: 'notStarted',
          airTotal: 0,
          hotelTotal: 0,
          carTotal: 0
        },
        createdAt: Date.now(),
        lastUpdated: Date.now()
      };

      // Enhance trip with immediate flight status data
      const enhancedTripData = await enhanceTripWithImmediateFlightStatus(tripData);
      
      // Send to real API
      // Creating trip with data
      // Silent data processing
      // Silent flight processing
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await authFetch(`${API_BASE_URL}/trips`, {
        method: 'POST',
        headers,
        body: JSON.stringify(enhancedTripData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      // Trip created successfully

      toast.success(`Trip "${tripData.tripName}" created successfully!`);
      
      // Call the callback to refresh the trips list
      if (onTripCreated) {
        onTripCreated(result.trip);
      }

      // Reset the form
      setUploadedFile(null);
      setParsedData(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Trip creation error:', error);
      toast.error('Failed to create trip: ' + error.message);
    }
  };

  const parsePDF = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const pdfDoc = await pdfjs.getDocument({ data: arrayBuffer }).promise;

          // Extract text from all pages
          let fullText = '';
          for (let i = 1; i <= pdfDoc.numPages; i++) {
            const page = await pdfDoc.getPage(i);
            const textContent = await page.getTextContent();
            fullText += textContent.items.map(item => item.str).join(' ') + '\n';
          }

          // Extracted PDF text

          // Use Nova Lite to parse the travel information
          const parsedData = await parseWithClaude(fullText, file.name);

          resolve(parsedData);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const extractTripNameFromFilename = (filename) => {
    // Extract trip name from filename
    const nameWithoutExt = filename.replace('.pdf', '');
    return nameWithoutExt.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Nova Lite parsing function via API Gateway -> Lambda -> Bedrock
  const parseWithClaude = async (text, filename) => {
    try {
      // Calling Nova Lite via Lambda

      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await authFetch(`${API_BASE_URL}/parse-bedrock`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          pdfText: text,
          filename: filename
        })
      });

      if (!response.ok) {
        throw new Error(`Nova Lite API error: ${response.status}`);
      }

      const result = await response.json();
      // Nova Lite API response

      if (result.success && result.data) {
        return result.data;
      } else {
        throw new Error(result.message || 'Unknown error from Nova Lite API');
      }

    } catch (error) {
      console.error('Nova Lite parsing error:', error);
      // Graceful degradation: when the AI parser is unavailable (e.g. the
      // parse-bedrock API errors), fall back to filename-based parsing so the
      // user still gets a trip created rather than a hard failure. PDF text
      // extraction failures are handled upstream and still surface as errors.
      console.warn('Falling back to filename-based parsing for', filename);
      return {
        tripName: extractTripNameFromFilename(filename),
        dates: {},
        flights: [],
        hotels: [],
        carRentals: [],
      };
    }
  };

  const handleClear = () => {
    setUploadedFile(null);
    setParsedData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginBottom: '20px'
    }}>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#1f2937',
        margin: '0 0 16px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <FileText size={20} />
        Upload PDF to Create Trip
      </h3>

      {!uploadedFile ? (
        <>
          <div
            role="button"
            tabIndex={0}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
            style={{
              border: '2px dashed #d1d5db',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
              background: '#f9fafb',
              display: 'block'
            }}
          >
            <Upload size={48} color="#9ca3af" style={{ marginBottom: '16px' }} />
            <p style={{
              fontSize: '16px',
              color: '#374151',
              margin: '0 0 8px 0'
            }}>
              Drop your travel PDF here or click to browse
            </p>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: '0'
            }}>
              Supports PDF files up to 10MB
            </p>
          </div>
          <input
            id="pdf-upload-input"
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </>
      ) : (
        <div>
          {/* File Selected */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px',
            background: '#f0f9ff',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileText size={20} color="#0369a1" />
              <div>
                <p style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#0c4a6e',
                  margin: '0 0 2px 0'
                }}>
                  {uploadedFile.name}
                </p>
                <p style={{
                  fontSize: '12px',
                  color: '#0369a1',
                  margin: '0'
                }}>
                  {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button
              onClick={handleClear}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                color: '#6b7280'
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Processing State */}
          {isParsing && (
            <div style={{
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f59e0b',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ fontWeight: '600', color: '#92400e' }}>
                  Processing PDF...
                </span>
              </div>
              <p style={{ color: '#92400e', fontSize: '14px', margin: '0' }}>
                Parsing travel details and creating your trip
              </p>
            </div>
          )}

          {/* Parsed Data Preview */}
          {parsedData && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <CheckCircle size={20} color="#16a34a" />
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#15803d',
                  margin: '0'
                }}>
                  PDF Parsed Successfully
                </h4>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#15803d' }}>Trip Name:</strong> {parsedData.tripName}
              </div>
              
              {parsedData.flights.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ color: '#15803d' }}>Flights:</strong> {parsedData.flights.length} found
                </div>
              )}
              
              {parsedData.hotels.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ color: '#15803d' }}>Hotels:</strong> {parsedData.hotels.length} found
                </div>
              )}
              
              {parsedData.carRentals.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ color: '#15803d' }}>Car Rentals:</strong> {parsedData.carRentals.length} found
                </div>
              )}

            </div>
          )}
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PDFUploader;
